import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Plus, MessageSquare } from 'lucide-react';
import { whatsappAPI, leadsAPI, propertiesAPI } from '../../services/api';
import ChatSidebar from './inbox/ChatSidebar';
import ChatWindow from './inbox/ChatWindow';
import ProfilePanel from './inbox/ProfilePanel';
import { readLocal, PIN_KEY, ARCHIVE_KEY, normalizePhone } from './inbox/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * WhatsApp-Web-style CRM Inbox
 * ──────────────────────────────────────────────────────
 * Three-panel layout (sidebar | chat | profile), real-time via WebSocket,
 * infinite older-messages load, emoji + attachment + template composer,
 * pin / archive / filter support (client-side). Backend contract unchanged.
 */
const WhatsAppInbox = () => {
  // ───────── Core state ─────────
  const [chats, setChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [messageLimit, setMessageLimit] = useState(60);

  const [contactInfo, setContactInfo] = useState(null);
  const [leadDetails, setLeadDetails] = useState(null);
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [labelsMeta, setLabelsMeta] = useState([]);

  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);

  const [showProfile, setShowProfile] = useState(() => {
    try { return window.matchMedia('(min-width: 768px)').matches; } catch { return true; }
  });
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatPhone, setNewChatPhone] = useState('');
  const [newChatName, setNewChatName] = useState('');

  const [templates, setTemplates] = useState([]);

  // Media upload
  const [mediaPreview, setMediaPreview] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Pin / archive (client-side persisted)
  const [pinned, setPinned] = useState(() => readLocal(PIN_KEY));
  const [archived, setArchived] = useState(() => readLocal(ARCHIVE_KEY));

  // Refs
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const selectedRef = useRef(null);

  useEffect(() => { selectedRef.current = selectedChat; }, [selectedChat]);

  // ───────── WebSocket ─────────
  // Sort comparator matching backend /inbox aggregation:
  // 1. Unknown chats (isUnknown OR no leadId+propertyId) pinned to top
  // 2. Within each tier, latest activity DESC
  const sortChats = useCallback((list) => {
    const isUnk = (c) => !!c.isUnknown || (!c.leadId && !c.propertyId);
    return [...list].sort((a, b) => {
      const ua = isUnk(a) ? 1 : 0;
      const ub = isUnk(b) ? 1 : 0;
      if (ua !== ub) return ub - ua;
      return new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0);
    });
  }, []);

  const connectWs = useCallback(() => {
    const proto = API_BASE?.startsWith('https') ? 'wss' : 'ws';
    const host = (API_BASE || '').replace(/^https?:\/\//, '');
    const url = `${proto}://${host}/api/whatsapp/ws`;
    try {
      wsRef.current = new WebSocket(url);
      wsRef.current.onopen = () => { setWsConnected(true); clearTimeout(reconnectRef.current); };
      wsRef.current.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === 'new_message') {
            const m = data.data;
            // Functional update — never reads stale `chats` from closure.
            // After mutation, re-sort so Unknowns stay pinned to top.
            setChats(prev => {
              const i = prev.findIndex(c => c.phone === m.phone);
              let next;
              if (i >= 0) {
                const wasSelected = selectedRef.current?.phone === m.phone;
                next = prev.slice();
                next[i] = {
                  ...next[i],
                  lastMessage: m.message || (m.caption || ''),
                  lastMessageAt: m.createdAt,
                  lastDirection: m.direction,
                  lastStatus: m.status,
                  // Latest message dictates current isUnknown / mapping
                  isUnknown: m.isUnknown !== undefined ? !!m.isUnknown : next[i].isUnknown,
                  leadId: m.leadId ?? next[i].leadId,
                  propertyId: m.propertyId ?? next[i].propertyId,
                  unreadCount: m.direction === 'received' && !wasSelected
                    ? (next[i].unreadCount || 0) + 1
                    : (wasSelected ? 0 : next[i].unreadCount),
                };
              } else {
                next = [{
                  phone: m.phone,
                  lastMessage: m.message || (m.caption || ''),
                  lastMessageAt: m.createdAt,
                  lastDirection: m.direction,
                  lastStatus: m.status,
                  recipientName: m.recipientName || m.phone,
                  propertyId: m.propertyId,
                  leadId: m.leadId,
                  isUnknown: m.isUnknown !== undefined ? !!m.isUnknown : (!m.leadId && !m.propertyId),
                  unreadCount: m.direction === 'received' ? 1 : 0,
                  messageCount: 1,
                }, ...prev];
              }
              return sortChats(next);
            });
            // Update open thread
            if (selectedRef.current?.phone === m.phone) {
              setMessages(prev => prev.some(x => x.id === m.id) ? prev : [...prev, m]);
            }
          }
          // ── Live status transition: queued → sent → delivered → read → failed ──
          if (data.type === 'status_update') {
            const m = data.data;
            if (!m?.id) return;
            if (selectedRef.current?.phone === m.phone) {
              setMessages(prev => prev.map(x => x.id === m.id ? { ...x, ...m } : x));
            }
            setChats(prev => prev.map(c => c.phone === m.phone
              ? { ...c, lastStatus: m.status, lastMessage: c.lastMessage }
              : c));
          }
        } catch { /* ignore parse errors */ }
      };
      wsRef.current.onclose = () => {
        setWsConnected(false);
        reconnectRef.current = setTimeout(connectWs, 3000);
      };
      wsRef.current.onerror = () => setWsConnected(false);
    } catch {
      setWsConnected(false);
      reconnectRef.current = setTimeout(connectWs, 5000);
    }
  }, []);

  useEffect(() => {
    connectWs();
    return () => {
      try { wsRef.current?.close(); } catch { /* ignore */ }
      clearTimeout(reconnectRef.current);
    };
  }, [connectWs]);

  // Keep-alive ping
  useEffect(() => {
    const id = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try { wsRef.current.send('ping'); } catch { /* ignore */ }
      }
    }, 30000);
    return () => clearInterval(id);
  }, []);

  // ───────── Data loads ─────────
  const loadChats = useCallback(async () => {
    try {
      setLoadingChats(true);
      const res = await whatsappAPI.getInboxChats();
      setChats(res.data.data || []);
    } catch (e) { console.error('loadChats', e); }
    finally { setLoadingChats(false); }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await whatsappAPI.getTemplates();
      setTemplates(res.data?.data || res.data || []);
    } catch { /* not critical */ }
  }, []);

  const loadLabels = useCallback(async () => {
    try {
      const res = await whatsappAPI.getLabels();
      setLabelsMeta(res.data?.data || res.data || []);
    } catch { /* not critical */ }
  }, []);

  useEffect(() => { loadChats(); loadTemplates(); loadLabels(); }, [loadChats, loadTemplates, loadLabels]);

  const loadMessages = useCallback(async (phone, limit = 60, { older = false } = {}) => {
    if (!phone) return;
    if (older) setLoadingOlder(true); else setLoadingMessages(true);
    try {
      const res = await whatsappAPI.getChatMessages(phone, limit);
      const data = res.data?.data || [];
      setMessages(data);
      setContactInfo(res.data?.contactInfo || null);
      setHasMoreOlder(data.length >= limit); // heuristic: full page means more may exist
    } catch (e) { console.error('loadMessages', e); }
    finally { older ? setLoadingOlder(false) : setLoadingMessages(false); }
  }, []);

  // When a chat is selected
  useEffect(() => {
    if (!selectedChat?.phone) {
      setMessages([]); setContactInfo(null);
      setLeadDetails(null); setPropertyDetails(null);
      return;
    }
    setMessageLimit(60);
    setHasMoreOlder(true);
    loadMessages(selectedChat.phone, 60);
    // Clear unread count optimistically
    setChats(prev => prev.map(c => c.phone === selectedChat.phone ? { ...c, unreadCount: 0 } : c));
  }, [selectedChat?.phone, loadMessages]);

  // When contactInfo lands, fetch full lead/property details
  useEffect(() => {
    const run = async () => {
      setLeadDetails(null); setPropertyDetails(null);
      if (!contactInfo?.id) return;
      setLoadingDetails(true);
      try {
        if (contactInfo.type === 'lead') {
          const res = await leadsAPI.getById(contactInfo.id);
          setLeadDetails(res.data?.data || res.data || null);
        } else if (contactInfo.type === 'property') {
          const res = await propertiesAPI.getById(contactInfo.id);
          setPropertyDetails(res.data?.data || res.data || null);
        }
      } catch (e) { console.error('loadDetails', e); }
      finally { setLoadingDetails(false); }
    };
    run();
  }, [contactInfo?.id, contactInfo?.type]);

  // ───────── Actions ─────────
  const loadOlder = useCallback(async () => {
    if (!selectedChat?.phone || !hasMoreOlder || loadingOlder) return;
    const nextLimit = messageLimit + 60;
    setMessageLimit(nextLimit);
    await loadMessages(selectedChat.phone, nextLimit, { older: true });
  }, [selectedChat?.phone, hasMoreOlder, loadingOlder, messageLimit, loadMessages]);

  const handleSendText = async (text) => {
    if (!selectedChat) return;
    setSending(true);
    try {
      const res = await whatsappAPI.sendMessage({
        phone: selectedChat.phone,
        message: text,
        propertyId: contactInfo?.type === 'property' ? contactInfo.id : null,
        leadId: contactInfo?.type === 'lead' ? contactInfo.id : null,
        source: 'manual',
      });
      if (!wsConnected && res.data) setMessages(prev => [...prev, res.data]);
      // Update sidebar row immediately
      setChats(prev => {
        const i = prev.findIndex(c => c.phone === selectedChat.phone);
        if (i < 0) return prev;
        const updated = [...prev];
        updated[i] = { ...updated[i], lastMessage: text, lastMessageAt: new Date().toISOString(), lastDirection: 'sent', lastStatus: 'sent' };
        const [row] = updated.splice(i, 1);
        return [row, ...updated];
      });
    } catch (e) {
      console.error('sendMessage', e);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleAttachClick = () => fileInputRef.current?.click();

  const onFileChosen = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'audio/mpeg', 'audio/ogg', 'application/pdf'];
    if (!allowed.includes(f.type)) { alert('Unsupported file type'); return; }
    if (f.size > 16 * 1024 * 1024) { alert('File exceeds 16MB'); return; }
    const isMedia = f.type.startsWith('image/') || f.type.startsWith('video/');
    const type = f.type.startsWith('image/') ? 'image'
      : f.type.startsWith('video/') ? 'video'
      : f.type.startsWith('audio/') ? 'audio' : 'document';
    if (isMedia) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setMediaPreview({ file: f, preview: ev.target.result, type, mimeType: f.type, name: f.name, size: f.size });
        setUploadOpen(true);
      };
      reader.readAsDataURL(f);
    } else {
      setMediaPreview({ file: f, preview: null, type, mimeType: f.type, name: f.name, size: f.size });
      setUploadOpen(true);
    }
  };

  const handleUpload = async (caption = '') => {
    if (!mediaPreview || !selectedChat) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', mediaPreview.file);
      fd.append('phone', selectedChat.phone);
      fd.append('caption', caption);
      fd.append('type', mediaPreview.type);
      if (contactInfo?.type === 'property') fd.append('propertyId', contactInfo.id);
      else if (contactInfo?.type === 'lead') fd.append('leadId', contactInfo.id);
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_BASE}/api/whatsapp/inbox/send-media`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const msg = typeof err.detail === 'string' ? err.detail : (err.detail?.message || 'Failed to send media');
        alert(msg); return;
      }
      const data = await r.json().catch(() => null);
      if (!wsConnected && data) setMessages(prev => [...prev, data]);
      setUploadOpen(false); setMediaPreview(null);
    } catch (e) {
      console.error('upload', e);
      alert('Failed to send media');
    } finally {
      setUploading(false);
    }
  };

  const handleNewChat = () => {
    if (!newChatPhone.trim()) { alert('Enter a phone number'); return; }
    const phone = normalizePhone(newChatPhone);
    const existing = chats.find(c => c.phone === phone);
    const chat = existing || {
      phone,
      recipientName: newChatName || phone,
      lastMessage: '',
      lastMessageAt: new Date().toISOString(),
      unreadCount: 0,
      messageCount: 0,
    };
    if (!existing) setChats(prev => [chat, ...prev]);
    setSelectedChat(chat);
    setShowNewChat(false); setNewChatPhone(''); setNewChatName('');
  };

  const handleRetry = async (messageId) => {
    if (!messageId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/whatsapp/inbox/retry/${messageId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(typeof err.detail === 'string' ? err.detail : 'Retry failed');
        return;
      }
      const updated = await res.json();
      setMessages(prev => prev.map(m => m.id === updated.id ? { ...m, ...updated } : m));
    } catch (e) {
      console.error('retry', e);
      alert('Retry failed');
    }
  };

  const handleCreateLeadFromUnknown = async () => {
    if (!selectedChat?.phone) return;
    const name = window.prompt('Name for the new lead (optional):', selectedChat.recipientName || '');
    if (name === null) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/whatsapp/inbox/create-lead`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: selectedChat.phone, name: name || '' }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(typeof err.detail === 'string' ? err.detail : 'Could not create lead');
        return;
      }
      const lead = await res.json();
      setContactInfo({ type: 'lead', id: lead.id, name: lead.hotel_name });
      setChats(prev => prev.map(c => c.phone === selectedChat.phone
        ? { ...c, leadId: lead.id, recipientName: lead.hotel_name }
        : c));
      setSelectedChat(prev => prev ? { ...prev, leadId: lead.id, recipientName: lead.hotel_name } : prev);
    } catch (e) {
      console.error('create-lead', e);
      alert('Could not create lead');
    }
  };

  const handleForwardMessages = async (items, targetPhones) => {
    if (!items?.length || !targetPhones?.length) return;
    const token = localStorage.getItem('token');
    const { withForwardMarker } = await import('./inbox/actions');
    // Fire in parallel per-target-per-message
    const jobs = [];
    for (const phone of targetPhones) {
      for (const msg of items) {
        const body = withForwardMarker(msg.message || msg.caption || '');
        jobs.push(fetch(`${API_BASE}/api/whatsapp/inbox/send`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone, message: body, source: 'manual' }),
        }).catch(e => ({ error: e })));
      }
    }
    const results = await Promise.all(jobs);
    const failed = results.filter(r => !r || (r && (r.error || (r.ok === false)))).length;
    const total = results.length;
    if (failed === total) {
      alert('Could not forward — all sends failed.');
    } else if (failed > 0) {
      alert(`Forwarded ${total - failed}/${total} — ${failed} failed.`);
    }
  };

  // ───────── Layout ─────────
  return (
    <div
      className="flex h-[calc(100vh-140px)] min-h-[500px] bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      data-testid="whatsapp-inbox-page"
    >
      {/* Hidden file input for attachments */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/jpeg,image/png,image/webp,video/mp4,audio/mpeg,audio/ogg,application/pdf"
        onChange={onFileChosen}
        data-testid="inbox-file-input"
      />

      {/* Left: sidebar */}
      <div className={`${selectedChat ? 'hidden md:flex' : 'flex'} w-full md:w-auto`}>
        <ChatSidebar
          chats={chats}
          loading={loadingChats}
          selectedPhone={selectedChat?.phone}
          onSelect={setSelectedChat}
          search={search} setSearch={setSearch}
          filter={filter} setFilter={setFilter}
          wsConnected={wsConnected}
          onNewChat={() => setShowNewChat(true)}
          onRefresh={loadChats}
          pinned={pinned} archived={archived}
          setPinned={setPinned} setArchived={setArchived}
        />
      </div>

      {/* Center: chat window */}
      <div className={`${selectedChat ? 'flex' : 'hidden md:flex'} flex-1 min-w-0`}>
        <ChatWindow
          selectedChat={selectedChat}
          contactInfo={contactInfo}
          messages={messages}
          loadingMessages={loadingMessages}
          loadingOlder={loadingOlder}
          hasMore={hasMoreOlder}
          onLoadOlder={loadOlder}
          onSend={handleSendText}
          sending={sending}
          onBack={() => setSelectedChat(null)}
          onRefresh={() => selectedChat && loadMessages(selectedChat.phone, messageLimit)}
          onToggleProfile={() => setShowProfile(s => !s)}
          fileInputRef={fileInputRef}
          onAttach={handleAttachClick}
          templates={templates}
          onRetry={handleRetry}
          onForwardMessages={handleForwardMessages}
          chats={chats}
          uploadOpen={uploadOpen}
          mediaPreview={mediaPreview}
          onUpload={handleUpload}
          onCancelUpload={() => { setUploadOpen(false); setMediaPreview(null); }}
          uploading={uploading}
        />
      </div>

      {/* Right: profile panel */}
      {selectedChat && (
        <div className={`${showProfile ? 'flex' : 'hidden'}`}>
          <ProfilePanel
            open={showProfile}
            onClose={() => setShowProfile(false)}
            selectedChat={selectedChat}
            contactInfo={contactInfo}
            leadDetails={leadDetails}
            propertyDetails={propertyDetails}
            loadingDetails={loadingDetails}
            labelsMeta={labelsMeta}
            onCreateLead={handleCreateLeadFromUnknown}
          />
        </div>
      )}

      {/* New Chat Modal */}
      {showNewChat && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="inbox-new-chat-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2 text-sm">
                <MessageSquare size={16} className="text-emerald-500" /> Start new chat
              </h3>
              <button onClick={() => setShowNewChat(false)} className="p-1 rounded hover:bg-gray-100"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone number <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  value={newChatPhone}
                  onChange={(e) => setNewChatPhone(e.target.value)}
                  placeholder="9876543210"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                  data-testid="inbox-new-chat-phone"
                />
                <p className="text-[11px] text-gray-400 mt-1">10-digit mobile; country code 91 will be added automatically.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name <span className="text-gray-400">(optional)</span></label>
                <input
                  type="text"
                  value={newChatName}
                  onChange={(e) => setNewChatName(e.target.value)}
                  placeholder="Contact name"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                  data-testid="inbox-new-chat-name"
                />
              </div>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setShowNewChat(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-white rounded-lg">Cancel</button>
              <button
                onClick={handleNewChat}
                disabled={!newChatPhone.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-500 text-white font-medium rounded-lg hover:bg-emerald-600 disabled:opacity-50"
                data-testid="inbox-new-chat-submit"
              >
                <Plus size={14} /> Start chat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppInbox;
